// Panel unico de notificaciones del header: una sola carcasa con pestanas
// para el listado combinado, los eventos del sistema y WhatsApp.
//
// El componente es de presentacion: no llama endpoints. Cada canal conserva
// su fuente y su logica en HeaderScreen, que le pasa los manejadores. Aqui
// solo vive el estado visual (pestana activa, historial expandido y la
// animacion de salida de una fila).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Tooltip } from "@mui/material";

import { NotificacionItem } from "./NotificacionItem";
import {
    agruparNotificaciones,
    agruparMensajesWhatsapp,
} from "../../../Helpers/Notificaciones/AgruparNotificaciones";
import {
    obtenerFechaNotificacion,
    obtenerEtiquetaDia,
} from "../../../Helpers/Notificaciones/FechaNotificacion";

// Filas visibles en la vista compacta. Cuenta grupos, no notificaciones sueltas.
const LIMITE_COMPACTO = 10;
// Debe coincidir con la transicion de .nt-item--saliendo en el SCSS.
const DURACION_SALIDA = 220;

export const CANAL_TODO = "todo";
export const CANAL_SISTEMA = "sistema";
export const CANAL_WHATSAPP = "whatsapp";

export const NotificacionesPanel = ({
    canal,
    onCambiarCanal,
    onCerrar,
    notificaciones = [],
    mensajesWhatsapp = [],
    onAbrirNotificacion,
    onEliminarNotificacion,
    onBorrarTodoSistema,
    onAbrirChat,
    onEliminarWhatsapp,
    onBorrarTodoWhatsapp,
    onBorrarTodo,
}) => {
    const [expandido, setExpandido] = useState(false);
    const [saliendo, setSaliendo] = useState([]);
    const contenedorRef = useRef(null);
    const temporizadoresRef = useRef([]);

    const esCombinado = canal === CANAL_TODO;

    // Agrupacion en cliente + fecha resuelta una sola vez por fila, para poder
    // ordenar el listado combinado y armar los separadores por dia.
    const enriquecer = useCallback((grupos, canalGrupo, obtenerClave) =>
        grupos.map((grupo) => {
            const { fecha } = obtenerFechaNotificacion(grupo, obtenerClave(grupo));
            return {
                ...grupo,
                canalGrupo,
                fechaGrupo: fecha,
                // La clave del grupo puede repetirse entre canales.
                claveUnica: `${canalGrupo}:${grupo.claveGrupo}`,
            };
        }), []);

    const gruposSistema = useMemo(
        () => enriquecer(agruparNotificaciones(notificaciones), CANAL_SISTEMA, (g) => g.idlog),
        [notificaciones, enriquecer],
    );

    const gruposWhatsapp = useMemo(
        () => enriquecer(agruparMensajesWhatsapp(mensajesWhatsapp), CANAL_WHATSAPP, (g) => g.idMensaje),
        [mensajesWhatsapp, enriquecer],
    );

    // El listado combinado se ordena por fecha. El orden es estable, asi que
    // mientras el backend no mande "fecha" se respeta el orden original.
    const gruposTodo = useMemo(
        () =>
            [...gruposSistema, ...gruposWhatsapp].sort(
                (a, b) => b.fechaGrupo.getTime() - a.fechaGrupo.getTime(),
            ),
        [gruposSistema, gruposWhatsapp],
    );

    const PESTANAS = [
        { clave: CANAL_TODO, etiqueta: "Todo", grupos: gruposTodo },
        { clave: CANAL_SISTEMA, etiqueta: "Sistema", grupos: gruposSistema },
        { clave: CANAL_WHATSAPP, etiqueta: "WhatsApp", grupos: gruposWhatsapp, Icono: WhatsAppIcon },
    ];

    const pestanaActiva = PESTANAS.find((p) => p.clave === canal) || PESTANAS[0];
    const grupos = pestanaActiva.grupos;
    const total = grupos.length;
    const visibles = expandido ? grupos : grupos.slice(0, LIMITE_COMPACTO);

    // Al cambiar de pestana se vuelve a la vista compacta.
    useEffect(() => {
        setExpandido(false);
    }, [canal]);

    // Cierre con Escape y con click fuera del panel.
    useEffect(() => {
        const alPresionarTecla = (evento) => {
            if (evento.key === "Escape") onCerrar && onCerrar();
        };
        const alHacerClickFuera = (evento) => {
            // Los iconos del header no cuentan como "fuera": ellos mismos
            // alternan el panel, si no se cerraria y volveria a abrir.
            const disparador =
                evento.target.closest &&
                evento.target.closest(".hd-header-notif-anchor, .hd-header-actions-whatsapp");
            if (disparador) return;

            if (contenedorRef.current && !contenedorRef.current.contains(evento.target)) {
                onCerrar && onCerrar();
            }
        };

        document.addEventListener("keydown", alPresionarTecla);
        document.addEventListener("mousedown", alHacerClickFuera);

        return () => {
            document.removeEventListener("keydown", alPresionarTecla);
            document.removeEventListener("mousedown", alHacerClickFuera);
        };
    }, [onCerrar]);

    // Limpieza de los temporizadores de la animacion de salida.
    useEffect(() => {
        const temporizadores = temporizadoresRef.current;
        return () => temporizadores.forEach((id) => clearTimeout(id));
    }, []);

    // Marca la fila como saliente y avisa al padre cuando termina la animacion.
    const eliminarConAnimacion = (grupo, alTerminar) => {
        setSaliendo((previos) => [...previos, grupo.claveUnica]);
        const temporizador = setTimeout(() => {
            setSaliendo((previos) => previos.filter((clave) => clave !== grupo.claveUnica));
            alTerminar();
        }, DURACION_SALIDA);
        temporizadoresRef.current.push(temporizador);
    };

    // En la pestana combinada cada fila se resuelve por su propio canal.
    const manejarAbrir = (grupo) => {
        if (grupo.canalGrupo === CANAL_WHATSAPP) {
            onAbrirChat && onAbrirChat(grupo);
            return;
        }
        // Abrir el grupo navega una vez y descarta todos sus idlog.
        onAbrirNotificacion && onAbrirNotificacion(grupo);
    };

    const manejarEliminar = (grupo) => {
        eliminarConAnimacion(grupo, () => {
            if (grupo.canalGrupo === CANAL_WHATSAPP) {
                onEliminarWhatsapp && onEliminarWhatsapp(grupo.idsMensaje);
            } else {
                onEliminarNotificacion && onEliminarNotificacion(grupo.idlogs);
            }
        });
    };

    const manejarBorrarTodo = () => {
        if (esCombinado) {
            onBorrarTodo && onBorrarTodo();
        } else if (canal === CANAL_WHATSAPP) {
            onBorrarTodoWhatsapp && onBorrarTodoWhatsapp();
        } else {
            onBorrarTodoSistema && onBorrarTodoSistema();
        }
    };

    // Separadores por dia sobre la lista visible.
    const filasConSeparador = [];
    let diaAnterior = null;
    visibles.forEach((grupo) => {
        const dia = obtenerEtiquetaDia(grupo.fechaGrupo);
        if (dia !== diaAnterior) {
            diaAnterior = dia;
            filasConSeparador.push({ tipo: "dia", clave: `dia-${dia}`, etiqueta: dia });
        }
        filasConSeparador.push({ tipo: "item", clave: grupo.claveUnica, grupo });
    });

    const titulo = expandido ? `Historial · ${pestanaActiva.etiqueta}` : "Notificaciones";

    const textoVacio = {
        [CANAL_TODO]: "No tienes notificaciones pendientes.",
        [CANAL_SISTEMA]: "No tienes notificaciones del sistema pendientes.",
        [CANAL_WHATSAPP]: "No tienes mensajes de WhatsApp pendientes.",
    }[pestanaActiva.clave];

    return (
        <div
            className={`nt-panel${expandido ? " nt-panel--expandido" : ""}`}
            ref={contenedorRef}
            role="dialog"
            aria-label="Notificaciones"
        >
            <span className="nt-panel-flecha" aria-hidden="true"></span>

            <div className="nt-panel-encabezado">
                <NotificationsIcon className="nt-panel-encabezado-icono" />
                <span className="nt-panel-encabezado-titulo">{titulo}</span>
                {total > 0 && (
                    <span className="nt-panel-encabezado-conteo">
                        {total} pendiente{total === 1 ? "" : "s"}
                    </span>
                )}
                {total > 0 && (
                    <Tooltip title={esCombinado ? "Borrar todo (ambos canales)" : "Borrar todo"}>
                        <button
                            type="button"
                            className="nt-panel-encabezado-boton"
                            onClick={manejarBorrarTodo}
                            aria-label="Borrar todo"
                        >
                            <DeleteSweepIcon />
                        </button>
                    </Tooltip>
                )}
            </div>

            <div className="nt-panel-pestanas" role="tablist">
                {PESTANAS.map(({ clave, etiqueta, grupos: gruposPestana, Icono }) => (
                    <button
                        key={clave}
                        type="button"
                        role="tab"
                        aria-selected={clave === pestanaActiva.clave}
                        className={`nt-pestana${clave === pestanaActiva.clave ? " nt-pestana--activa" : ""}`}
                        onClick={() => onCambiarCanal(clave)}
                    >
                        {Icono && <Icono />}
                        {etiqueta}
                        <span className="nt-pestana-conteo">{gruposPestana.length}</span>
                    </button>
                ))}
            </div>

            <div className={`nt-panel-listado scroll-4${expandido ? " nt-panel-listado--expandido" : ""}`}>
                {total === 0 ? (
                    <div className="nt-panel-vacio">
                        <NotificationsNoneIcon />
                        <b>Todo al dia</b>
                        <span>{textoVacio}</span>
                    </div>
                ) : (
                    filasConSeparador.map((fila) =>
                        fila.tipo === "dia" ? (
                            <div key={fila.clave} className="nt-panel-dia">
                                {fila.etiqueta}
                            </div>
                        ) : (
                            <NotificacionItem
                                key={fila.clave}
                                notificacion={fila.grupo}
                                canal={fila.grupo.canalGrupo}
                                // En el listado combinado se marca de que canal viene cada fila.
                                mostrarCanal={esCombinado}
                                saliendo={saliendo.includes(fila.clave)}
                                onAbrir={manejarAbrir}
                                onEliminar={manejarEliminar}
                            />
                        ),
                    )
                )}
            </div>

            {total > 0 && (
                <div className="nt-panel-pie">
                    <span className="nt-panel-pie-conteo">
                        {expandido
                            ? `${total} en total`
                            : `${Math.min(LIMITE_COMPACTO, total)} de ${total}`}
                    </span>
                    {(expandido || total > LIMITE_COMPACTO) && (
                        <button
                            type="button"
                            className="nt-panel-pie-boton"
                            onClick={() => setExpandido(!expandido)}
                        >
                            {expandido ? (
                                <>
                                    <ChevronLeftIcon /> Ver menos
                                </>
                            ) : (
                                <>
                                    Ver todo el historial <ChevronRightIcon />
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
